<template>
  <teleport v-if="isPossibleToEdit" to="body">
    <div class="lf-btn-action lf-btn-action--calendar lf-btn-action--active">
      <div ref="fpCalendar" class="lf-btn-action lf-btn-action--calendar lf-btn-action--active flatpickr-input">
        <span class="lf-btn-action__icon icon-calendar"></span>
        <div class="lf-btn-action__label">Select test date</div>
        <div class="lf-calendar-datepicker"></div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { useStore } from '@/store';
import { isSsr } from '@/utilities/Utilities';
import 'flatpickr/dist/flatpickr.css';
import { AddonGameplayModel } from '@/components/addons/gameplay/Model';
import { GameCalendarModel, GameCalendarState } from '@/components/games/calendar/Model';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
export default defineComponent({
  name: 'CalendarGameInitializer',
  setup() {
    const store = useStore();
    const campaignModel = store.getters['campaign/model'];
    const isReady = ref(true);
    const fpCalendar = ref<HTMLElement | null>(null);
    const isReloading = ref(false);
    let initialized = false;
    let fp: Instance;

    const isDemo = computed(() => {
      if (isSsr()) return false;

      return (store.getters['utility/path'] ?? window.location.href).includes('/campaign/view/demo');
    });

    const isPossibleToEdit = computed(() => {
      if (!isDemo.value) {
        return false;
      }
      return typeof campaignModel.state.isEditModeActive !== 'undefined';
    });

    const fetchTestDateFromState = () => {
      let calendarGameState: GameCalendarState | undefined;

      campaignModel.state.flowPages.forEach((pageModel) => {
        pageModel.getAddons<AddonGameplayModel>('gameplay').forEach(async (gameplayModel) => {
          const calendarGameModel = gameplayModel.state.settings?.game;
          if (calendarGameModel instanceof GameCalendarModel) {
            calendarGameState = calendarGameModel?.state;

            if (calendarGameState?.general.test.enabled && calendarGameState?.general.test.date) {
              await store.dispatch('global/setGameTestDate', calendarGameState?.general.test.date);
              fp.setDate(store.getters['global/gameTestDate']);
              isReady.value = true;
            } else {
              fp.setDate(store.getters['global/gameTestDate']);
              isReady.value = true;
            }
            isReady.value = true;
          }
        });
      });
    };

    const initFlatpickr = () => {
      if (!(fpCalendar.value instanceof HTMLElement)) {
        return;
      }

      fp = flatpickr(fpCalendar.value, {
        onChange: () => {
          store.dispatch('global/setGameTestDate', fp.selectedDates[0]);
        },
        static: false,
        disableMobile: true
      });

      fp.calendarContainer.classList.add('leadfamly-calendar-datepicker');

      fetchTestDateFromState();

      initialized = true;
    };

    watch(
      () => [fpCalendar.value, isPossibleToEdit.value],
      () => {
        if (isPossibleToEdit.value && !initialized) {
          initFlatpickr();
        }
      },
      {
        immediate: true
      }
    );

    return {
      isDemo,
      isReady,
      fpCalendar,
      isReloading,
      isPossibleToEdit,
      campaignModel
    };
  }
});
</script>

<style lang="scss">
.lf-btn-action {
  position: fixed;
  right: 90px;
  bottom: 30px;
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #131313;
  line-height: 43px;
  font-size: 16px;
  color: #ffffff;
  text-align: center;
  cursor: pointer;

  ul {
    position: absolute;
    transform: translateX(-105%) translateY(-100%);
    background-color: #333;
    color: #fff;
    list-style: none;
    padding: 0;
    margin: 0;
    width: 160px;
  }
  li {
    line-height: 1.8rem;
    list-style: none;
    background-color: #3e3e3e !important;
    color: #959595 !important;
    font-size: 12px !important;
    font-family: Gilroy, Helvetica, Arial, sans-serif;
    padding: 6px 10px !important;
    text-align: left;
    transition: all 0.3s ease;
    &:hover {
      background-color: rgba(255, 255, 255, 0.15) !important;
      color: #fff !important;
    }
  }
}

.lf-btn-action__icon {
  opacity: 0.5;
  transition: opacity 175ms linear;
}

.lf-btn-action--active .lf-btn-action__icon {
  opacity: 1;
}

.lf-btn-action__label {
  display: block;
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%);
  padding: 4px 5px;
  background: #202020;
  border-radius: 4px;
  font-family: 'Gilroy', Helvetica, Arial, sans-serif;
  font-size: 11px;
  line-height: 11px;
  letter-spacing: 0;
  white-space: nowrap;
  color: #d8d8d8;
  opacity: 0;
  transition: opacity 150ms linear;
  pointer-events: none;
}

.lf-btn-action:hover .lf-btn-action__label {
  opacity: 1;
}

.lf-btn-action select {
  display: none;
}

.lf-btn-action .select2 {
  position: absolute;
  right: 100%;
  bottom: 0;
  margin-right: 5px;
  width: 160px !important;
}

.lf-btn-action .select2-selection {
  display: none;
}

.lf-btn-action--reload {
  right: 150px;
}

.flatpickr-disabled {
  opacity: 0.5;
}

.flatpickr-calendar.leadfamly-calendar-datepicker {
  width: 290px !important;
  height: 290px !important;
  border-radius: 0 !important;
  border: 1px solid #e5e5e5 !important;
}

.leadfamly-calendar-datepicker {
  width: 247px;
  padding: 4px;
  font-family: Gilroy, Helvetica, Arial, sans-serif;
  border-radius: 0 !important;
  -webkit-box-shadow: none;
  box-shadow: none;
}

.flatpickr-months {
  padding: 4px 16px 8px !important;
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: bold !important;
}

.flatpickr-current-month {
  .flatpickr-monthDropdown-months {
    font-weight: bold !important;
    margin-right: 4px !important;

    &:hover {
      background: transparent !important;
    }
  }

  .numInputWrapper,
  .flatpickr-monthDropdown-months {
    font-size: 14px !important;
  }

  .numInputWrapper {
    width: 7ch !important;

    &:hover {
      background: transparent !important;
    }
  }
}

.flatpickr-current-month input.cur-year {
  font-weight: bold !important;
}

.numInputWrapper:hover {
  background: transparent !important;
}

.flatpickr-weekdays span.flatpickr-weekday {
  color: rgba(0, 0, 0, 0.38) !important;
  font-size: 12px !important;
}

.flatpickr-months {
  .flatpickr-next-month:hover svg,
  .flatpickr-prev-month:hover svg {
    color: rgba(0, 0, 0, 0.9) !important;
    fill: rgba(0, 0, 0, 0.9) !important;
  }
}

.flatpickr-innerContainer {
  padding: 0 12px !important;
}

.flatpickr-days {
  width: 100% !important;
}

.dayContainer {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

.flatpickr-day {
  color: rgba(0, 0, 0, 0.87) !important;
  cursor: pointer !important;
  width: 32px !important;
  flex-basis: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  margin: 1px 2px !important;
  line-height: 32px !important;
  font-size: 12px !important;
}

.flatpickr-calendar.static {
  top: auto;
  position: absolute;
  bottom: 30px;
  right: -21px;
}
</style>
